// v2 shell — Database view: a master-detail workbench instance (createWorkbench(),
// workbench.js) over the tenant's SQL table names (rail) with a real read-only
// data grid on selection (detail): schema strip, sortable/paged/filtered rows.
//
// KEPT from the pre-workbench version of this module (M8/M12 waves): the
// table-name list fetch (api.dbTables()), the windowed rail render (a big
// tenant can have ~1000 tables -- painting them all upfront janks the view),
// and the viewport-lazy batched row/column-count subtitles (api.dbTableStats(),
// IntersectionObserver-driven, debounced+chunked so scrolling never triggers an
// N+1 request storm). Only the RENDER HOST changed: rows now live in the
// workbench's own `.wb-list` (rendered via `renderRow`/`onFilter`) instead of a
// bespoke `#v2DbList`, and the search box is the workbench's own
// `.wb-filter-input` instead of a bespoke `#v2DbSearch`. workbench.js has no
// built-in concept of "windowed" rendering (setList() always paints its full
// filtered set), so the windowing here is reimplemented on top of it: `onFilter`
// returns only the first `_shown` of the full filtered+sorted match set, and a
// scroll sentinel (same IntersectionObserver recipe as before) grows `_shown`
// and re-calls `wb.setList()` as the user scrolls. Because workbench.js always
// fully rebuilds `.wb-list`'s HTML (no incremental append), row stats are
// rendered INLINE from `_statsMemo` on every render pass (not DOM-patched after
// the fact like the old module did) -- a resolved/pending value survives any
// later rebuild (window growth, a stats batch for OTHER rows landing) for free.
//
// NEW (this task): selecting a row opens a real per-table grid in the
// workbench's detail pane -- schema strip (collapsible column chips, click to
// sort), a native <table class="wb-grid"> (sticky header, sort indicators,
// per-column filter popovers whose active filters render as removable chips),
// and a footer with pagination + a page-size select. Detail head carries the
// table name, a custom/system chip (best-effort from the SAME row-stats memo
// the rail already resolves -- no extra fetch), the row/column dimensions, and
// three icon actions: Copy name is real (clipboard); Export CSV downloads the
// current sort + filters as a CSV attachment (api.downloadDbCsv -- hosted
// bearer fetch->Blob->anchor, local direct-URL anchor; toasts on error or on
// the 100,000-row truncation cap).
//
// Ask AI: opens the workbench's own assistant drawer
// (assistant-drawer.js's renderAssistant(), same primitive Code view's
// Explain/Improve uses -- wired via the `drawer:{render}` config passed to
// createWorkbench() below) with `ctx.kind="table"`, the table's name, its
// is_custom flag (the SAME rail stats memo _detailHeadHtml() already reads --
// no extra fetch), and its column NAMES (from the schema this module already
// fetched to build the grid -- detail.columns is `{name,type,nullable,...}[]`,
// buildTablePrompt() only wants the names). The drawer itself renders question
// chips and streams on click; nothing here duplicates that logic.
//
// Grid data contract (api.js/dbSchema, dbData; server/_routes_db_inspector.py):
// dbSchema(name) -> {table, columns:[{name,type,nullable,maxLength}]} (type is
// a raw SQL Server type string, e.g. "nvarchar"/"int"/"decimal"). dbData(name,
// {page,pageSize,orderBy,dir,where}) -> {table, rows, totalCount, page,
// pageSize} where `rows` is an array of plain objects keyed by column name and
// EVERY cell value is a lossy string -- '' is server-side NULL (also used for
// any raw '|'/newline/tab in the source value). `where` is `col:op:value;...`
// with server ops eq|contains|gt|lt (buildWhereParam() below builds this from
// the UI's active filter list, which already stores server-side op names).
import { api } from "./api.js";
import { esc, escAttr, state } from "./state.js";
import { notify } from "./toast.js";
import { createWorkbench } from "./workbench.js";
import { iconHtml } from "./icons.js";
import { renderAssistant, teardownAssistantDrawer } from "./assistant-drawer.js";

const ROW_ICON = "ic-database"; // same glyph as this view's own nav entry (shell-v2.js NAV_MODEL)

// Row stats (F2, carried over): batch cap must match the server's own
// _STATS_BATCH_CAP (server/_routes_db_inspector.py) -- a separate constant
// here only affects CLIENT-side chunking; the server clamps independently.
const STATS_BATCH_CAP = 25;
const STATS_DEBOUNCE_MS = 80;
const _PENDING = Symbol("pending");

// Windowed rendering (carried over): the table-name list can be ~1000 rows on
// a big tenant. Only the first _WINDOW rows of the filtered+sorted set are
// rendered; a scroll sentinel grows the window as the user scrolls.
const _WINDOW = 60;

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZES = [50, 100, 200, 500];
const NUMERIC_TYPES = new Set([
  "int", "decimal", "numeric", "float", "money", "bigint", "smallint", "tinyint", "bit",
]);
// UI filter ops map 1:1 onto the server's own op vocabulary (_parse_where in
// _routes_db_inspector.py) -- stored directly as the server op name, so
// buildWhereParam() never needs a second translation table.
const FILTER_OPS = [
  { value: "contains", label: "contains" },
  { value: "eq", label: "equals" },
  { value: "gt", label: "greater than" },
  { value: "lt", label: "less than" },
];

let _mount = null;
let _wb = null;

let _all = [];        // last-fetched (or test-injected) table-name list
let _fetchToken = 0;  // guards a stale in-flight fetch racing setData() or a fresh call
let _prewarming = false; // true while the boot idle-prewarm fetch is in flight (prewarmDatabaseTables)
let _loading = false; // true while renderDatabaseInto() is waiting on _loadTables()/prewarm
let _loadError = null; // last _loadTables() failure message, or null

// Row stats memo -- tableName -> _PENDING | {rows, columns, is_custom} | (absent = never
// requested / a resolved-null failure, see _statsFailed below). _statsFailed is a
// SEPARATE set (not memoized as part of _statsMemo) so a failed lookup renders "—" for
// the CURRENT render pass without being memoized as a permanent failure -- see
// _observeVisibleRows()'s own comment for why it's also excluded from immediate
// re-observation (avoids a failing table retry-looping every time ITS OWN failure
// triggers a rail re-render).
let _statsMemo = new Map();
let _statsFailed = new Set();
let _pendingNames = [];
let _statsDebounceTimer = null;
// Bumped by resetDatabaseStats() on every tenant switch -- an in-flight
// _fetchStatsChunk() request captures this before awaiting and discards its
// own result if it no longer matches once the response lands.
let _statsEpoch = 0;
let _statsObserver = null;
let _sentinelObserver = null;

let _visible = [];   // current filtered+sorted {id,name} match set (full, not windowed)
let _shown = 0;       // how many of _visible are rendered into the DOM so far
let _lastQuery = null; // the rail filter query _visible/_shown were last computed for

// Detail (open table) state -- one open table at a time. _openToken guards a
// stale in-flight fetch (initial open or a sort/filter/page re-fetch) racing a
// newer selection or a tenant switch, mirroring code-view.js's own _openToken.
let _openToken = 0;
let _detail = null; // { name, columns, page, pageSize, orderBy, dir, filters, totalCount, rows, schemaCollapsed, detailApi }

// ---------------------------------------------------------------------------
// Pure helpers (exported, unit-tested directly -- no DOM required)
// ---------------------------------------------------------------------------

/** {col,op,value}[] -> "col:op:value;col2:op:value" (server `where` param shape). */
export function buildWhereParam(filters) {
  return (filters || []).map((f) => `${f.col}:${f.op}:${f.value}`).join(";");
}

/** Every grid cell value is a lossy string where '' means server-side NULL. */
export function cellDisplay(value) {
  return value === "" || value == null ? "—" : String(value);
}

/** Which SQL Server column types right-align in the grid (schema `type` string). */
export function isNumericType(sqlType) {
  return NUMERIC_TYPES.has(String(sqlType || "").toLowerCase());
}

/** ceil(total/pageSize), floored at 1 -- guards a zero/undefined pageSize too. */
export function pageCount(total, pageSize) {
  const size = pageSize > 0 ? pageSize : 1;
  return Math.max(1, Math.ceil((Number(total) || 0) / size));
}

// ---------------------------------------------------------------------------
// Rail: windowed table-name list + search + lazy stats subtitles
// ---------------------------------------------------------------------------

// workbench.js's own `onFilter(items, query)` hook. `items` is whatever was
// last passed to wb.setList() -- always the FULL {id,name} list here (see
// _refreshRail()). Recomputes the full filtered+sorted match set only when the
// query actually changed (a window-growth or stats-driven re-render calls
// wb.setList() again with the SAME query, and must not reset _shown back to
// _WINDOW when it does) -- OR when the underlying item COUNT changed even
// though the query didn't. That second condition matters because
// renderDatabaseInto()'s own cold-load path calls _refreshRail() TWICE with
// the SAME (default "") query: once immediately with `_all` still empty (the
// "Loading tables…" placeholder), and again once _loadTables() resolves with
// the real (non-empty) list -- and the E2E test hook's setData() call is the
// same shape (an empty rail settled to "No tables found." is itself a prior
// _onFilter("") call that already stamped _lastQuery=""). Without this,
// query==="" both times means the fast path never re-slices `items`, so
// `_visible` stays permanently stuck at its first, empty computation and the
// real/injected rows never render -- a query-string comparison alone isn't a
// safe proxy for "the item set didn't change".
let _lastItemsCount = -1;
function _onFilter(items, query) {
  if (query !== _lastQuery || items.length !== _lastItemsCount) {
    _lastQuery = query;
    _lastItemsCount = items.length;
    const q = (query || "").trim().toLowerCase();
    _visible = (q ? items.filter((it) => it.name.toLowerCase().includes(q)) : items.slice())
      .sort((a, b) => a.name.localeCompare(b.name));
    _shown = Math.min(_WINDOW, _visible.length);
    _statsFailed.clear(); // a genuine new search is a fair moment to retry any failed lookups
  }
  return _visible.slice(0, _shown);
}

// Renders one row's .fsub INLINE from the CURRENT _statsMemo/_statsFailed
// state -- unlike the pre-workbench version, there is no post-render DOM
// patch: workbench.js rebuilds `.wb-list` wholesale on every wb.setList()
// call, so the row HTML itself must already reflect whatever is currently
// known. _PENDING -> unobtrusive "…"; a memoized failure -> "—" (a real
// lookup failure, never confused with "still loading"); a resolved value ->
// "custom table · N rows · N columns" (is_custom) or "N rows · N columns";
// not yet requested (row never scrolled into view) -> no subtitle at all.
function _statsSubHtml(name) {
  const stats = _statsMemo.get(name);
  if (stats === _PENDING) return `<span class="fsub fsub-pending">…</span>`;
  if (stats) {
    const rowsTxt = typeof stats.rows === "number" ? `${stats.rows.toLocaleString()} rows` : "—";
    const colsTxt = typeof stats.columns === "number" ? `${stats.columns.toLocaleString()} columns` : "—";
    return `<span class="fsub">${esc((stats.is_custom ? "custom table · " : "") + `${rowsTxt} · ${colsTxt}`)}</span>`;
  }
  if (_statsFailed.has(name)) return `<span class="fsub">—</span>`;
  return "";
}

function _railRowHtml(item) {
  const stats = _statsMemo.get(item.name);
  const customCls = stats && stats !== _PENDING && stats.is_custom ? " db-row-custom" : "";
  return `<div class="wb-row${customCls}" role="option" aria-selected="false" data-id="${escAttr(item.id)}" data-seg="table" title="${escAttr(item.name)}">`
    + `<span class="fic"><span class="ic ic-sm ${ROW_ICON}"></span></span>`
    + `<span class="fmeta"><span class="fname">${esc(item.name)}</span>${_statsSubHtml(item.name)}</span>`
    + `<span class="fright"><span class="ic ic-sm ic-chevron-right"></span></span>`
    + `</div>`;
}

function _headSub() {
  const tenant = state.tenant || "—";
  if (_loadError) return `${tenant} · —`; // fetch FAILED -- unknown, never a misleading "0"
  const count = _all.length;
  return `${tenant} · ${count} table${count === 1 ? "" : "s"}`;
}

// The failed case renders a Retry control alongside the message -- mirrors
// the DETAIL pane's own error+Retry strip (_renderDetailError()'s
// `.wb-detail-error` / `.wb-detail-error-actions` / `btn wb-retry` markup
// convention below), scoped to the rail with its own `wb-rail-` class names
// so _patchEmptyState()'s querySelector (rooted at the rail's `.wb-list`)
// can never collide with the DETAIL pane's identically-purposed button in
// the SAME document.
function _emptyMessage() {
  if (_loading) return "Loading tables…";
  if (_loadError) {
    return `<div class="wb-rail-error">Failed to load tables: ${esc(_loadError)}`
      + `<div class="wb-rail-error-actions"><button type="button" class="btn wb-rail-retry">Retry</button></div>`
      + `</div>`;
  }
  if (_lastQuery) return `No tables match "${esc(_lastQuery)}".`;
  return "No tables found.";
}

// Overwrites workbench.js's own generic "No matches." text with an honest,
// state-specific message (loading / failed / search-empty / genuinely empty)
// once the list is actually empty. Mirrors code-view.js's
// _patchTableEmptyState() precedent (a second, later-registered listener
// re-patches after workbench's own render finishes). The failed case also
// wires its Retry button here -- innerHTML replacement above always
// discards any prior listener along with the old button node, so this runs
// on every patch pass rather than once.
function _patchEmptyState() {
  const list = _mount && _mount.querySelector(".wb-list");
  if (!list || _visible.length) return;
  const emptyEl = list.querySelector(".wb-empty");
  if (!emptyEl) return;
  emptyEl.innerHTML = _emptyMessage();
  if (_loadError) {
    const retryBtn = emptyEl.querySelector(".wb-rail-retry");
    if (retryBtn) retryBtn.addEventListener("click", () => {
      _loading = true;
      _loadError = null;
      _refreshRail(); // immediately show "Loading tables…" -- mirrors the cold-load precedent
      _loadTables();
    });
  }
}

// Appends the scroll sentinel (if more rows remain beyond the current
// window) and (re-)observes every rendered-but-unresolved row for lazy stats.
// Called after every wb.setList() call this module makes directly.
function _afterRailRender() {
  if (!_mount) return;
  const list = _mount.querySelector(".wb-list");
  if (!list) return;
  if (_shown < _visible.length) {
    list.insertAdjacentHTML("beforeend", `<div class="v2-db-sentinel" aria-hidden="true" style="height:1px"></div>`);
    _observeSentinel(list.querySelector(".v2-db-sentinel"));
  }
  _observeVisibleRows(list);
  _patchEmptyState();
  const sub = _mount.querySelector(".content-head .ch-sub");
  if (sub) sub.textContent = _headSub();
}

// The one place that (re-)paints the rail from _all -- always passes the FULL
// {id,name} list into wb.setList(); _onFilter() above narrows it down to the
// current window. Used for every data change (initial load, prewarm landing,
// test injection) AND for window growth / stats resolution (same items
// reference contents, so _onFilter's query-unchanged fast path is taken).
function _refreshRail() {
  if (!_wb) return;
  _wb.setList(_all.map((name) => ({ id: name, name })));
  _afterRailRender();
}

function _growWindow() {
  if (_shown >= _visible.length) return;
  _shown = Math.min(_shown + _WINDOW, _visible.length);
  _refreshRail();
}

// One shared IntersectionObserver on the list's scroll sentinel, rooted at
// `.wb-list` itself (the actual scrolling container in the workbench layout --
// unlike the pre-workbench version, there is no separate `.v2-col-primary`
// scroll ancestor to reach for). rootMargin prefetches the next window
// ~400px before the sentinel is actually visible.
function _observeSentinel(sentinel) {
  if (!sentinel) return;
  if (!_sentinelObserver) {
    const root = (_mount && _mount.querySelector(".wb-list")) || null;
    _sentinelObserver = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) _growWindow();
    }, { root, rootMargin: "400px 0px" });
  }
  _sentinelObserver.observe(sentinel);
}

function _ensureStatsObserver() {
  if (_statsObserver) return _statsObserver;
  const root = (_mount && _mount.querySelector(".wb-list")) || null;
  _statsObserver = new IntersectionObserver(_onRowIntersect, { root, rootMargin: "200px 0px" });
  return _statsObserver;
}

// Observes every currently-rendered row that hasn't been requested/resolved
// yet. `_statsFailed` names are deliberately EXCLUDED here (not just
// resolved/pending ones): a failed table's stats resolution itself triggers
// a rail re-render (see _fetchStatsChunk), and if that re-render immediately
// re-observed the same still-visible row, IntersectionObserver would report
// it as already-intersecting and re-fire synchronously -- a tight retry loop
// against a persistently-failing table. Excluding failed names breaks that
// loop; they get a fresh chance on the next genuinely new search (_onFilter
// clears _statsFailed on a query change) or tenant switch.
function _observeVisibleRows(list) {
  const target = list || (_mount && _mount.querySelector(".wb-list"));
  if (!target) return;
  const observer = _ensureStatsObserver();
  target.querySelectorAll(".wb-row[data-id]").forEach((row) => {
    const name = row.getAttribute("data-id");
    if (name && _statsMemo.get(name) === undefined && !_statsFailed.has(name)) observer.observe(row);
  });
}

function _onRowIntersect(entries) {
  let any = false;
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;
    const name = entry.target.getAttribute("data-id");
    if (!name) continue;
    _statsObserver.unobserve(entry.target); // one-shot per element -- re-renders re-observe fresh nodes
    if (_requestStatsFor(name)) any = true;
  }
  // One rail re-render for the whole intersection burst (not per row) -- gives
  // instant "…" pending feedback without a render-per-row storm on a fast scroll.
  if (any) _refreshRail();
}

// Returns true iff `name` was newly enqueued (already pending/resolved -> false).
function _requestStatsFor(name) {
  if (_statsMemo.get(name) !== undefined) return false;
  _statsMemo.set(name, _PENDING);
  _statsFailed.delete(name);
  _pendingNames.push(name);
  if (_statsDebounceTimer) clearTimeout(_statsDebounceTimer);
  // Debounced: a screenful of rows intersects in one burst (initial render, or
  // a fast scroll) -- coalescing that burst into ONE request (chunked at
  // STATS_BATCH_CAP) instead of one per row is the whole point of this view
  // being "no N+1 storm".
  _statsDebounceTimer = setTimeout(_flushPendingStats, STATS_DEBOUNCE_MS);
  return true;
}

function _flushPendingStats() {
  _statsDebounceTimer = null;
  if (!_pendingNames.length) return;
  const names = _pendingNames;
  _pendingNames = [];
  for (let i = 0; i < names.length; i += STATS_BATCH_CAP) {
    _fetchStatsChunk(names.slice(i, i + STATS_BATCH_CAP));
  }
}

async function _fetchStatsChunk(names) {
  const epoch = _statsEpoch; // captured before the await -- see _statsEpoch's own comment
  let data;
  try {
    data = await api.dbTableStats(names);
  } catch {
    if (epoch !== _statsEpoch) return; // superseded by a tenant switch while this was in flight
    for (const n of names) { _statsMemo.delete(n); _statsFailed.add(n); }
    _refreshRail();
    _maybeRefreshDetailChip(names);
    return;
  }
  if (epoch !== _statsEpoch) return;
  const stats = (data && data.stats) || {};
  for (const n of names) {
    const value = Object.prototype.hasOwnProperty.call(stats, n) ? stats[n] : null;
    if (value === null) { _statsMemo.delete(n); _statsFailed.add(n); }
    else { _statsMemo.set(n, value); _statsFailed.delete(n); }
  }
  _refreshRail();
  _maybeRefreshDetailChip(names);
}

// Best-effort live upgrade: if the table currently open in the detail pane is
// among the names that just resolved, refresh its custom/system chip -- no
// extra fetch, just consults the SAME memo the rail uses.
function _maybeRefreshDetailChip(names) {
  if (_detail && names.includes(_detail.name)) _refreshDetailHead(_detail);
}

// Tenant-switch reset (exported, called from shell-v2.js's resetViewGuards(),
// same place every other per-tenant view's own reset lives). Bumping
// _statsEpoch closes the in-flight race: a request already dispatched for the
// OLD tenant must not write its (stale) result once it resolves after a
// switch -- _fetchStatsChunk() checks this epoch before each of its write
// sites. Also drops the windowed-render state and any open detail pane so the
// next tenant starts completely fresh.
//
// teardownAssistantDrawer(): a tenant switch re-mounts the whole workbench
// (createWorkbench() rebuilds `.wb-drawer` from scratch on the next
// renderDatabaseInto()), which blows away the OLD drawer's DOM without ever
// going through workbench.js's closeDrawer() -- the assistant drawer's own
// MutationObserver-based close detection (assistant-drawer.js) never fires
// for a node that's simply gone, so this force-teardown is needed here too.
// Mirrors code-view.js's resetCodeView() precedent exactly.
export function resetDatabaseStats() {
  teardownAssistantDrawer();
  _statsEpoch++;
  _statsMemo.clear();
  _statsFailed.clear();
  _pendingNames = [];
  if (_statsDebounceTimer) { clearTimeout(_statsDebounceTimer); _statsDebounceTimer = null; }
  if (_sentinelObserver) _sentinelObserver.disconnect();
  if (_statsObserver) _statsObserver.disconnect();
  // Both observers are rooted at `.wb-list`, a node createWorkbench() replaces
  // wholesale on every renderDatabaseInto() -- nulling the cached instances
  // (not just disconnecting them) forces _ensureStatsObserver()/_observeSentinel()
  // to reconstruct fresh ones against the NEW tenant's live `.wb-list` on next
  // render, instead of returning the old, now-detached-root instance forever.
  _sentinelObserver = null;
  _statsObserver = null;
  _visible = [];
  _shown = 0;
  _lastQuery = null;
  _lastItemsCount = -1;
  _all = [];
  _loadError = null;
  _loading = false;
  _fetchToken++;
  _prewarming = false;
  _detail = null;
  _openToken++;
}

// ---------------------------------------------------------------------------
// Data loading (rail)
// ---------------------------------------------------------------------------

async function _loadTables() {
  const token = ++_fetchToken;
  try {
    const data = await api.dbTables();
    if (token !== _fetchToken) return; // superseded by a newer call or a setData() injection
    _all = Array.isArray(data && data.tables) ? data.tables : [];
    _loadError = null;
  } catch (e) {
    if (token !== _fetchToken) return;
    _all = [];
    _loadError = String((e && e.message) || e);
  }
  _loading = false;
  _refreshRail();
}

// Boot idle-prewarm: fetch the table-name list into the module cache off the
// boot-critical path (fired via requestIdleCallback from main.js, local mode).
// Best-effort: a failure just falls back to the normal on-open fetch.
export function prewarmDatabaseTables() {
  if (_all.length || _prewarming) return;
  _prewarming = true;
  const token = ++_fetchToken;
  api.dbTables()
    .then((data) => {
      if (token !== _fetchToken) return; // superseded by a real fetch / setData / tenant switch
      _all = Array.isArray(data && data.tables) ? data.tables : [];
      _loadError = null;
      _loading = false;
      if (_mount) _refreshRail(); // user opened Database while we were in flight -- paint the cache now
    })
    .catch(() => {
      // Best-effort prewarm failed. If the user already opened the Database
      // view while this was still in flight, renderDatabaseInto()'s cold-load
      // path (see its own comment) skipped its OWN _loadTables() call,
      // trusting this promise's .then() above to paint the rail once it
      // landed -- with no .then() firing, the rail is stuck on "Loading
      // tables…" forever unless something else recovers it. Fall back to a
      // real _loadTables() call ONLY when the view is mounted and nothing
      // else already populated `_all` in the meantime (e.g. the E2E test
      // hook's setData()) -- that turns the silent failure into the honest
      // "Failed to load tables…" (with its own Retry control) or a quiet
      // success, instead of an infinite spinner. Returning the promise here
      // keeps `_prewarming` true for its duration (see .finally() below),
      // consistent with this whole call being "a boot-time table fetch still
      // in flight". If the view ISN'T mounted yet, there's nothing to
      // recover -- the normal on-open path will call _loadTables() itself.
      if (_mount && !_all.length) return _loadTables();
    })
    .finally(() => { _prewarming = false; });
}

// ---------------------------------------------------------------------------
// Detail: schema strip + grid + filters + pagination
// ---------------------------------------------------------------------------

// best-effort: whatever the rail's own row-stats memo currently knows for
// `name` -- shared by the detail head's custom/system chip AND the Ask AI
// button's ctx.table.isCustom (no extra fetch either way).
function _isCustomTable(name) {
  const stats = _statsMemo.get(name);
  return !!(stats && stats !== _PENDING && stats.is_custom);
}

function _detailHeadHtml(detail, loaded) {
  const isCustom = _isCustomTable(detail.name);
  const chip = isCustom ? "custom" : "system";
  const dims = loaded ? `${detail.totalCount.toLocaleString()} rows × ${detail.columns.length} cols` : "…";
  return `<span class="wb-name">${esc(detail.name)}</span>`
    + `<span class="bchip">${esc(chip)}</span>`
    + `<span class="fcol">${esc(dims)}</span>`
    + `<span class="wb-det-actions">`
    + `<button type="button" class="icon-btn wb-export-btn" title="Export CSV" aria-label="Export CSV">${iconHtml("download", { size: "sm" })}</button>`
    + `<button type="button" class="icon-btn wb-askai-btn" title="Ask AI" aria-label="Ask AI">${iconHtml("sparkles", { size: "sm" })}</button>`
    + `<button type="button" class="icon-btn wb-copy-btn" title="Copy name" aria-label="Copy name">${iconHtml("copy", { size: "sm" })}</button>`
    + `</span>`;
}

function _wireDetailHeadActions(detail) {
  const head = _mount && _mount.querySelector(".wb-detail-head");
  if (!head) return;
  const exportBtn = head.querySelector(".wb-export-btn");
  if (exportBtn) {
    exportBtn.addEventListener("click", async () => {
      try {
        const truncated = await api.downloadDbCsv(detail.name, {
          orderBy: detail.orderBy, dir: detail.dir, where: buildWhereParam(detail.filters),
        });
        if (truncated) notify("Export truncated at 100,000 rows.", "info");
      } catch (err) {
        notify(err.message || "Export CSV failed.", "error");
      }
    });
  }
  const askAiBtn = head.querySelector(".wb-askai-btn");
  if (askAiBtn) {
    askAiBtn.addEventListener("click", () => {
      if (!_wb) return;
      _wb.openDrawer({
        kind: "table",
        label: detail.name,
        table: {
          name: detail.name,
          isCustom: _isCustomTable(detail.name),
          columns: detail.columns.map((col) => col.name),
        },
      });
    });
  }
  const copyBtn = head.querySelector(".wb-copy-btn");
  if (copyBtn) {
    copyBtn.addEventListener("click", async () => {
      try { await navigator.clipboard.writeText(detail.name); }
      catch { /* clipboard permission/context unavailable -- silent no-op, same precedent as code-view.js */ }
    });
  }
}

function _refreshDetailHead(detail) {
  const head = _mount && _mount.querySelector(".wb-detail-head");
  if (!head) return;
  head.innerHTML = _detailHeadHtml(detail, !!detail._loaded);
  _wireDetailHeadActions(detail);
}

function _schemaStripHtml(detail) {
  const chips = detail.columns.map((col) =>
    `<span class="chip wb-schema-chip" data-col="${escAttr(col.name)}" title="${escAttr(col.type)}${col.nullable ? "" : ", not null"}">`
    + `${esc(col.name)} · ${esc(col.type)}</span>`
  ).join("");
  return `<div class="wb-schema-head">`
    + `<button type="button" class="wb-schema-toggle" aria-expanded="${detail.schemaCollapsed ? "false" : "true"}">`
    + `${iconHtml(detail.schemaCollapsed ? "chevron-right" : "chevron-down", { size: "sm" })}`
    + `<span>Schema · ${detail.columns.length} column${detail.columns.length === 1 ? "" : "s"}</span>`
    + `</button></div>`
    + `<div class="wb-schema"${detail.schemaCollapsed ? " hidden" : ""}>${chips || `<span class="wb-schema-empty">No columns.</span>`}</div>`;
}

function _filterOpLabel(op) {
  const found = FILTER_OPS.find((o) => o.value === op);
  return found ? found.label : op;
}

function _filterChipsHtml(detail) {
  if (!detail.filters.length) return "";
  const chips = detail.filters.map((f, i) =>
    `<span class="chip wb-filter-chip">${esc(f.col)} ${esc(_filterOpLabel(f.op))} "${esc(f.value)}"`
    + `<button type="button" class="wb-filter-chip-x" data-idx="${i}" title="Remove filter" aria-label="Remove filter">${iconHtml("x", { size: "sm" })}</button></span>`
  ).join("");
  return `<div class="wb-filter-chips">${chips}</div>`;
}

function _headerCellHtml(col, detail) {
  const isSort = detail.orderBy === col.name;
  const glyph = isSort ? (detail.dir === "desc" ? " ▾" : " ▴") : "";
  const hasFilter = detail.filters.some((f) => f.col === col.name);
  const existing = detail.filters.find((f) => f.col === col.name);
  return `<th data-col="${escAttr(col.name)}">`
    + `<span class="wb-th-label" data-sort-col="${escAttr(col.name)}">${esc(col.name)}${glyph}</span>`
    + `<button type="button" class="wb-th-filter-btn${hasFilter ? " active" : ""}" data-filter-col="${escAttr(col.name)}" title="Filter ${escAttr(col.name)}" aria-label="Filter ${escAttr(col.name)}">${iconHtml("filter", { size: "sm" })}</button>`
    + `<div class="wb-filter-pop" data-pop-col="${escAttr(col.name)}" hidden>`
    + `<select class="wb-filter-op">${FILTER_OPS.map((o) => `<option value="${escAttr(o.value)}"${existing && existing.op === o.value ? " selected" : ""}>${esc(o.label)}</option>`).join("")}</select>`
    + `<input type="text" class="wb-filter-val" placeholder="Value" autocomplete="off" value="${existing ? escAttr(existing.value) : ""}" />`
    + `<button type="button" class="btn btn-xs wb-filter-apply">Apply</button>`
    + `</div>`
    + `</th>`;
}

function _bodyRowHtml(row, columns) {
  return `<tr>${columns.map((col) => {
    const display = cellDisplay(row ? row[col.name] : "");
    const cls = [isNumericType(col.type) ? "num" : "", display === "—" ? "wb-cell-muted" : ""].filter(Boolean).join(" ");
    return `<td${cls ? ` class="${cls}"` : ""}>${esc(display)}</td>`;
  }).join("")}</tr>`;
}

function _gridHtml(detail) {
  if (!detail.columns.length) return `<div class="wb-detail-empty">No columns.</div>`;
  const thead = `<thead><tr>${detail.columns.map((col) => _headerCellHtml(col, detail)).join("")}</tr></thead>`;
  const tbody = detail.rows.length
    ? `<tbody>${detail.rows.map((row) => _bodyRowHtml(row, detail.columns)).join("")}</tbody>`
    : `<tbody><tr><td class="wb-grid-empty" colspan="${detail.columns.length}">No rows${detail.filters.length ? " match these filters" : ""}.</td></tr></tbody>`;
  return `<div class="wb-grid-scroll"><table class="wb-grid">${thead}${tbody}</table></div>`;
}

function _footerHtml(detail) {
  // detail.page is assigned straight from the server's echoed `page` (no
  // `typeof === "number"` guard, unlike totalCount above) and interpolated
  // into HTML -- coerce + esc() defensively so a malformed/non-numeric echo
  // can't break comparisons (NaN <= 1 is always false, disabling neither nav
  // button) or inject raw HTML. Normal integer case renders identically.
  const page = Number(detail.page) || 1;
  const totalPages = pageCount(detail.totalCount, detail.pageSize);
  return `<div class="wb-grid-footer">`
    + `<button type="button" class="icon-btn wb-page-prev" title="Previous page" aria-label="Previous page"${page <= 1 ? " disabled" : ""}>${iconHtml("chevron-left", { size: "sm" })}</button>`
    + `<span class="wb-page-label">page ${esc(page)} of ${esc(totalPages)}</span>`
    + `<button type="button" class="icon-btn wb-page-next" title="Next page" aria-label="Next page"${page >= totalPages ? " disabled" : ""}>${iconHtml("chevron-right", { size: "sm" })}</button>`
    + `<select class="wb-page-size">${PAGE_SIZES.map((n) => `<option value="${n}"${n === detail.pageSize ? " selected" : ""}>${n} / page</option>`).join("")}</select>`
    + `<span class="wb-total-count">${detail.totalCount.toLocaleString()} total</span>`
    + `</div>`;
}

function _renderDetailBody(body, detail) {
  body.classList.remove("wb-loading");
  body.innerHTML = _schemaStripHtml(detail) + _filterChipsHtml(detail) + _gridHtml(detail) + _footerHtml(detail);
  _wireDetailBody(body, detail);
}

function _applySort(body, detail, col) {
  if (!col) return;
  if (detail.orderBy === col) detail.dir = detail.dir === "asc" ? "desc" : "asc";
  else { detail.orderBy = col; detail.dir = "asc"; }
  detail.page = 1;
  _refetchDetail(body, detail);
}

function _closeAllFilterPops(body) {
  body.querySelectorAll(".wb-filter-pop").forEach((p) => p.setAttribute("hidden", ""));
}

function _wireDetailBody(body, detail) {
  const toggle = body.querySelector(".wb-schema-toggle");
  if (toggle) toggle.addEventListener("click", () => {
    detail.schemaCollapsed = !detail.schemaCollapsed;
    _renderDetailBody(body, detail);
  });

  body.querySelectorAll(".wb-schema-chip").forEach((chip) => {
    chip.addEventListener("click", () => _applySort(body, detail, chip.getAttribute("data-col")));
  });
  body.querySelectorAll(".wb-th-label").forEach((label) => {
    label.addEventListener("click", () => _applySort(body, detail, label.getAttribute("data-sort-col")));
  });

  body.querySelectorAll(".wb-th-filter-btn").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const col = btn.getAttribute("data-filter-col");
      const pop = body.querySelector(`.wb-filter-pop[data-pop-col="${CSS.escape(col)}"]`);
      const wasOpen = pop && !pop.hasAttribute("hidden");
      _closeAllFilterPops(body);
      if (pop && !wasOpen) {
        pop.removeAttribute("hidden");
        const valInput = pop.querySelector(".wb-filter-val");
        if (valInput) valInput.focus();
      }
    });
  });

  body.querySelectorAll(".wb-filter-apply").forEach((btn) => {
    btn.addEventListener("click", () => {
      const pop = btn.closest(".wb-filter-pop");
      const col = pop.getAttribute("data-pop-col");
      const op = pop.querySelector(".wb-filter-op").value;
      const value = pop.querySelector(".wb-filter-val").value.trim();
      pop.setAttribute("hidden", "");
      if (!value) return;
      detail.filters = detail.filters.filter((f) => f.col !== col);
      detail.filters.push({ col, op, value });
      detail.page = 1;
      _refetchDetail(body, detail);
    });
  });

  body.querySelectorAll(".wb-filter-val").forEach((input) => {
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault(); e.stopPropagation();
        input.closest(".wb-filter-pop").querySelector(".wb-filter-apply").click();
      } else if (e.key === "Escape") {
        e.preventDefault(); e.stopPropagation();
        input.closest(".wb-filter-pop").setAttribute("hidden", "");
      }
    });
  });

  body.querySelectorAll(".wb-filter-chip-x").forEach((btn) => {
    btn.addEventListener("click", () => {
      const idx = Number(btn.getAttribute("data-idx"));
      detail.filters = detail.filters.filter((_, i) => i !== idx);
      detail.page = 1;
      _refetchDetail(body, detail);
    });
  });

  const prevBtn = body.querySelector(".wb-page-prev");
  if (prevBtn) prevBtn.addEventListener("click", () => {
    if (detail.page > 1) { detail.page -= 1; _refetchDetail(body, detail); }
  });
  const nextBtn = body.querySelector(".wb-page-next");
  if (nextBtn) nextBtn.addEventListener("click", () => {
    if (detail.page < pageCount(detail.totalCount, detail.pageSize)) { detail.page += 1; _refetchDetail(body, detail); }
  });
  const sizeSel = body.querySelector(".wb-page-size");
  if (sizeSel) sizeSel.addEventListener("change", () => {
    detail.pageSize = Number(sizeSel.value) || DEFAULT_PAGE_SIZE;
    detail.page = 1;
    _refetchDetail(body, detail);
  });
}

function _renderDetailError(body, detail, message, opts) {
  const hasFilters = detail.filters.length > 0;
  body.classList.remove("wb-loading");
  body.innerHTML = `<div class="wb-detail-error">`
    + `Could not load this table${opts.initial ? "" : "'s data"}. ${esc(message)}`
    + `<div class="wb-detail-error-actions">`
    + `<button type="button" class="btn wb-retry">Retry</button>`
    + (hasFilters ? `<button type="button" class="btn wb-clear-filters">Clear filters</button>` : "")
    + `</div></div>`;
  const retryBtn = body.querySelector(".wb-retry");
  if (retryBtn) retryBtn.addEventListener("click", () => {
    if (opts.initial) _openTable({ id: detail.name, name: detail.name }, detail.detailApi);
    else _refetchDetail(body, detail);
  });
  const clearBtn = body.querySelector(".wb-clear-filters");
  if (clearBtn) clearBtn.addEventListener("click", () => {
    detail.filters = [];
    detail.page = 1;
    _refetchDetail(body, detail);
  });
}

// Re-fetches ONLY the data page (schema doesn't change mid-session) for the
// currently-open table -- sort/filter/page/page-size changes all funnel here.
function _refetchDetail(body, detail) {
  const myOpen = _openToken;
  body.classList.add("wb-loading");
  api.dbData(detail.name, {
    page: detail.page,
    pageSize: detail.pageSize,
    orderBy: detail.orderBy,
    dir: detail.dir,
    where: buildWhereParam(detail.filters),
  }).then((data) => {
    if (myOpen !== _openToken || _detail !== detail) return; // superseded by a newer row-open
    detail.rows = (data && data.rows) || [];
    detail.totalCount = typeof (data && data.totalCount) === "number" ? data.totalCount : detail.totalCount;
    detail.page = (data && data.page) || detail.page;
    detail.pageSize = (data && data.pageSize) || detail.pageSize;
    _renderDetailBody(body, detail);
    _refreshDetailHead(detail);
  }).catch((e) => {
    if (myOpen !== _openToken || _detail !== detail) return;
    _renderDetailError(body, detail, String((e && e.message) || e), { initial: false });
  });
}

// Opens `item` (a rail row, {id, name}) in the detail pane: head first (name
// only, no fetch needed), then fetches schema + first data page IN PARALLEL,
// then renders the full schema-strip + grid + footer.
function _openTable(item, detailApi) {
  const myOpen = ++_openToken;
  const name = item.name;
  _detail = {
    name,
    columns: [],
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    orderBy: "",
    dir: "asc",
    filters: [],
    totalCount: 0,
    rows: [],
    schemaCollapsed: false,
    detailApi,
  };
  const detail = _detail;

  detailApi.setHead(_detailHeadHtml(detail, false));
  _wireDetailHeadActions(detail);

  const body = document.createElement("div");
  body.className = "wb-db-detail";
  body.innerHTML = `<div class="wb-detail-empty">Loading…</div>`;
  // Closes any open per-column filter popover on a click elsewhere in the
  // detail body. Attached ONCE per open table (`body` itself persists across
  // sort/filter/page re-renders -- only its innerHTML is replaced), so this
  // uses a plain property assignment rather than addEventListener to avoid
  // accumulating a duplicate listener on every _renderDetailBody() call.
  body.onclick = (e) => {
    if (e.target.closest(".wb-filter-pop") || e.target.closest(".wb-th-filter-btn")) return;
    _closeAllFilterPops(body);
  };
  detailApi.setBody(body);

  Promise.all([api.dbSchema(name), api.dbData(name, { page: 1, pageSize: DEFAULT_PAGE_SIZE })])
    .then(([schema, data]) => {
      if (myOpen !== _openToken) return; // superseded mid-await -- a newer selection or tenant switch
      detail.columns = (schema && schema.columns) || [];
      detail.rows = (data && data.rows) || [];
      detail.totalCount = typeof (data && data.totalCount) === "number" ? data.totalCount : 0;
      detail.page = (data && data.page) || 1;
      detail.pageSize = (data && data.pageSize) || DEFAULT_PAGE_SIZE;
      detail._loaded = true;
      _renderDetailBody(body, detail);
      _refreshDetailHead(detail);
    })
    .catch((e) => {
      if (myOpen !== _openToken) return;
      _renderDetailError(body, detail, String((e && e.message) || e), { initial: true });
    });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * One-time build + fetch: builds the workbench (rail + detail), wired for
 * client-side search + row-select-opens-grid. Called once per page load / per
 * tenant (shell-v2.js's _databaseViewRendered guard, reset on tenant switch
 * by resetViewGuards() -- see resetDatabaseStats() above).
 */
export async function renderDatabaseInto(container) {
  if (!container) return;
  _mount = container;
  _detail = null;
  _openToken++;
  _lastQuery = null;
  _lastItemsCount = -1;
  _visible = [];
  _shown = 0;

  _wb = createWorkbench(container, {
    header: { title: "Database", sub: `${state.tenant || "—"} · …` },
    onFilter: _onFilter,
    renderRow: _railRowHtml,
    onSelect: (item, detailApi) => _openTable(item, detailApi),
    drawer: { title: "Assistant", render: (ctx, body) => renderAssistant(ctx, body) },
  });

  // Re-run the windowing/lazy-stats/empty-state after-render pass once
  // workbench's OWN filter-input listener finishes ITS render. workbench.js's
  // `_onFilterInput` calls only its own internal `_renderList()` -- it has no
  // concept of this module's windowing (the scroll sentinel) or lazy stats
  // (the IntersectionObserver), so without this second listener a typed
  // search query would rebuild `.wb-list`'s HTML with neither the sentinel
  // appended nor any newly-visible row observed for its stats subtitle, and
  // scrolling past 60 filtered matches would silently stop loading more.
  // _afterRailRender() already calls _patchEmptyState() as one of its own
  // steps (mirrors code-view.js's _patchTableEmptyState() precedent for the
  // empty-state text specifically), so this one listener covers both.
  const filterInputEl = container.querySelector(".wb-filter-input");
  if (filterInputEl) filterInputEl.addEventListener("input", _afterRailRender);

  if (_all.length) {
    _refreshRail();
  } else {
    _loading = true;
    _refreshRail(); // paints the (still-empty) rail; _patchEmptyState shows "Loading tables…"
    if (!_prewarming) await _loadTables();
    // else: a boot prewarm is already in flight -- its own .then() calls
    // _refreshRail() the moment it lands (now that _mount is set above).
  }

  // Deterministic E2E hook (mirrors code-view.js's window.__test_code):
  // setData() injects a known table-name list without a live fetch, bumping
  // _fetchToken so it always wins over an in-flight real fetch, and
  // re-renders through the SAME _refreshRail() path a real render uses.
  window.__test_database = {
    setData(rows) {
      _fetchToken++;
      _all = Array.isArray(rows) ? rows : [];
      _loadError = null;
      _loading = false;
      _refreshRail();
    },
    getState() {
      return { count: _all.length, filteredCount: _visible.length, query: _lastQuery || "" };
    },
    // Detail-grid state, for a future grid E2E to assert against.
    getDetailState() {
      if (!_detail) return null;
      return {
        name: _detail.name,
        page: _detail.page,
        pageSize: _detail.pageSize,
        orderBy: _detail.orderBy,
        dir: _detail.dir,
        filters: _detail.filters.slice(),
        totalCount: _detail.totalCount,
        columnCount: _detail.columns.length,
        rowCount: _detail.rows.length,
      };
    },
  };
}
